// KARS-KI Support-Assistent – API-Schicht.
//
// Backend: backend/internal/assistant (Spec 2026-11-04).
//   GET  /protected/assistant/context
//   POST /protected/assistant/chat            → text/event-stream
//   GET  /protected/assistant/conversations/{id}
//   POST /protected/assistant/messages/{id}/rating
//   POST /protected/assistant/hints/{key}/dismiss
//
// Der Chat streamt als Server-Sent Events über fetch + ReadableStream (ein
// EventSource kann keinen POST-Body senden und keine Cookies im Cross-Origin
// Dev-Setup zuverlässig mitschicken).

import { API_BASE_URL } from '@/config'
import { getRequestGeneric, postRequestGeneric } from '@/services/backendApiGeneric'
import { parseSseChunk, type SseEvent } from '@/services/sse'

export type InsightSeverity = 'info' | 'hint' | 'warn'

export interface AssistantRoute {
  name: string
  query?: Record<string, string>
  params?: Record<string, string>
}

export interface AssistantInsight {
  key: string
  severity: InsightSeverity
  title: string
  text: string
  route?: AssistantRoute
  icon?: string
}

export interface AssistantContext {
  enabled: boolean
  greeting: { firstName: string, timeOfDay: 'morning' | 'day' | 'evening' }
  insights: AssistantInsight[]
  suggestions: string[]
  conversationId: number | null
  knowledgeVersion: string
}

export interface AssistantSource {
  id: string
  title: string
  route?: AssistantRoute
}

export interface AssistantStoredMessage {
  id: number
  role: 'user' | 'assistant'
  content: string
  sources?: AssistantSource[] | null
  rating?: number | null
  createdAt?: string
}

export interface AssistantConversation {
  id: number
  title?: string | null
  messages: AssistantStoredMessage[]
}

export type AssistantStep = 'knowledge' | 'context' | 'thinking' | 'writing'

export interface ChatRequest {
  conversationId?: number | null
  message: string
  locale?: string
  page?: { routeName: string, query?: Record<string, unknown> }
}

export interface ChatHandlers {
  onMeta?: (meta: { conversationId: number, userMessageId: number, assistantMessageId: number }) => void
  onStatus?: (status: { step: AssistantStep, text?: string }) => void
  onDelta?: (text: string) => void
  onSources?: (sources: AssistantSource[]) => void
  onDone?: (done: { content: string, suggestions: string[] }) => void
  onError?: (message: string) => void
}

export class AssistantHttpError extends Error {
  constructor (public status: number, message: string) {
    super(message)
    this.name = 'AssistantHttpError'
  }
}

export function getAssistantContext (): Promise<AssistantContext> {
  return getRequestGeneric('/protected/assistant/context')
}

export function getAssistantConversation (id: number): Promise<AssistantConversation> {
  return getRequestGeneric(`/protected/assistant/conversations/${id}`)
}

export function rateAssistantMessage (id: number, rating: 1 | -1 | 0): Promise<unknown> {
  return postRequestGeneric(`/protected/assistant/messages/${id}/rating`, { rating }, true)
}

export function dismissAssistantHint (key: string, days = 7): Promise<unknown> {
  return postRequestGeneric(`/protected/assistant/hints/${encodeURIComponent(key)}/dismiss`, { days }, true)
}

// ── SSE ─────────────────────────────────────────────────────────────────────

function dispatch (ev: SseEvent, handlers: ChatHandlers) {
  let payload: any = null
  try {
    payload = ev.data ? JSON.parse(ev.data) : null
  } catch {
    payload = { text: ev.data }
  }
  switch (ev.event) {
    case 'meta': {
      handlers.onMeta?.(payload)
      break
    }
    case 'status': {
      handlers.onStatus?.(payload)
      break
    }
    case 'delta': {
      handlers.onDelta?.(typeof payload?.text === 'string' ? payload.text : '')
      break
    }
    case 'sources': {
      handlers.onSources?.(Array.isArray(payload) ? payload : (payload?.sources ?? []))
      break
    }
    case 'done': {
      handlers.onDone?.({ content: payload?.content ?? '', suggestions: payload?.suggestions ?? [] })
      break
    }
    case 'error': {
      handlers.onError?.(payload?.message ?? 'Unbekannter Fehler')
      break
    }
    default: {
      break
    }
  }
}

/**
 * Sendet eine Nachricht und verarbeitet den Ereignisstrom. Löst
 * AssistantHttpError aus, wenn der Server den Chat ablehnt (429, 503, 400).
 */
export async function streamAssistantChat (body: ChatRequest, handlers: ChatHandlers, signal?: AbortSignal): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/protected/assistant/chat`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
    body: JSON.stringify(body),
    signal,
  })

  if (!response.ok) {
    let message = `Der Assistent ist gerade nicht erreichbar (HTTP ${response.status}).`
    try {
      const json = await response.json()
      if (json?.message) {
        message = json.message
      }
    } catch { /* kein JSON-Körper */ }
    throw new AssistantHttpError(response.status, message)
  }
  if (!response.body) {
    throw new AssistantHttpError(0, 'Der Browser unterstützt kein Streaming.')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''
  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) {
        break
      }
      buffer += decoder.decode(value, { stream: true })
      const parsed = parseSseChunk(buffer)
      buffer = parsed.rest
      for (const ev of parsed.events) {
        dispatch(ev, handlers)
      }
    }
    buffer += decoder.decode()
    if (buffer.trim()) {
      const parsed = parseSseChunk(buffer + '\n\n')
      for (const ev of parsed.events) {
        dispatch(ev, handlers)
      }
    }
  } finally {
    reader.releaseLock()
  }
}

export { parseSseChunk, type SseEvent } from '@/services/sse'
